<template>
  <div class="coin-purchase-view container py-4">
    <!-- Header -->
    <div class="text-center mb-4">
      <h2 class="fw-bold mb-2">
        <font-awesome-icon icon="coins" class="me-2 text-warning" />
        Quittungskauf
      </h2>
      <p class="text-muted mb-0">
        Kaufe Quittungen für deine Bereiche
      </p>
    </div>

    <!-- Progress Steps -->
    <ProgressSteps
      :steps="steps"
      :current-step="currentStep"
      @go-to-step="goToStep"
    />

    <!-- Step Content -->
    <div class="card">
      <div class="card-body p-3">
        <!-- Step 1: Coin Packages -->
        <CoinSelectionStep
          v-if="currentStep === 0"
          :coin-packages="coinPackages"
          :selected-package="selectedPackage"
          :coupon-result="couponResult"
          @package-selected="selectPackage"
          @coupon-validated="onCouponValidated"
          @coupon-cleared="onCouponCleared"
        />

        <!-- Step 2: Billing Data -->
        <div v-if="currentStep === 1" class="sales-step-content">
          <div class="text-center mb-3">
            <font-awesome-icon icon="file-invoice" size="3x" class="text-info mb-2" />
            <h4>Rechnungsdaten</h4>
            <p class="text-muted mb-0">
              <span v-if="isFreeUpgrade">Rechnungsdaten</span>
              <span v-else>Bitte gib deine Rechnungsdaten ein</span>
            </p>
          </div>

          <BillingForm
            :billing-data="billingData"
            :is-free-upgrade="isFreeUpgrade"
            :show-tax-fields="true"
            :show-save-option="false"
            :show-card="true"
            @proceed-to-payment="proceedToPayment"
            @billing-data-changed="onBillingDataChanged"
          />
        </div>

        <!-- Step 3: Payment -->
        <CoinOrderSummaryStep
          v-if="currentStep === 2"
          :selected-package="selectedPackage"
          :price-data="priceData"
          :billing-data="billingData"
          :has-filled-billing-data="hasFilledBillingData"
          :coupon-result="couponResult"
          :coupon-code="couponCode"
          :is-free-upgrade="isFreeUpgrade"
          @payment-method-changed="onPaymentMethodChanged"
          @terms-changed="onTermsChanged"
        />

        <!-- Navigation -->
        <div class="d-flex justify-content-between mt-4">
          <button 
            v-if="currentStep > 0"
            @click="previousStep" 
            class="btn btn-outline-secondary"
          >
            <font-awesome-icon icon="arrow-left" class="me-2" />
            Zurück
          </button>
          <div v-else></div>
          
          <button 
            v-if="currentStep < steps.length - 1"
            @click="nextStep" 
            class="btn btn-primary"
            :disabled="!canProceed"
          >
            Weiter
            <font-awesome-icon icon="arrow-right" class="ms-2" />
          </button>
          <button 
            v-else
            @click="processPurchase" 
            class="btn btn-success"
            :disabled="!canProceed || !allTermsAccepted"
          >
            <font-awesome-icon icon="check" class="me-2" />
            Jetzt verbindlich kaufen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTeamsStore } from '@/stores/teams'
import { pricingApi } from '@/api/pricing'
import {
  showSuccess,
  showError,
} from '@/services/notification'

// Composables
import { useBillingData } from './composables/useBillingData'
import { useCoupon } from './composables/useCoupon'
import { usePricing } from './composables/usePricing'
import { useWizardSteps } from './composables/useWizardSteps'

// Components
import ProgressSteps from './components/ProgressSteps.vue'
import BillingForm from './components/BillingForm.vue'
import CoinSelectionStep from './coins/CoinSelectionStep.vue'
import CoinOrderSummaryStep from './coins/CoinOrderSummaryStep.vue'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()

const loading = ref(true)
const error = ref(null)
const selectedPackage = ref(null)
const coinPackages = ref([])
const paymentMethod = ref('paypal')
const allTermsAccepted = ref(false)

const steps = [
  {
    title: 'Paket wählen',
    description: 'Wähle das Quittungspaket'
  },
  {
    title: 'Rechnungsdaten',
    description: 'Gib deine Daten ein'
  },
  {
    title: 'Zahlung',
    description: 'Bestätige den Kauf'
  }
]

// Use composables
const { billingData, hasFilledBillingData, validateBillingData, loadLastBillingData, getBillingDataForApi } = useBillingData()
const { couponCode, couponLoading, couponResult, validateCoupon, getCouponDescription, onCouponCodeChange } = useCoupon()
const { priceData, formatCurrency, calculatePriceData } = usePricing()
const { currentStep, canNavigateToStep, goToStep, nextStep, previousStep, isStepActive, isStepCompleted, isStepDisabled } = useWizardSteps(steps)

const mapInvoiceDataToApi = (data = {}) => ({
  firstName: data.firstName || '',
  lastName: data.lastName || '',
  company: data.company || data.companyName || '',
  street: data.street || '',
  number: data.number || data.houseNumber || '',
  zip: data.zip || data.zipCode || data.postalCode || '',
  city: data.city || '',
  country: data.country || 'Deutschland',
  email: data.email || '',
  vatId: data.vatId || '',
  taxId: data.taxId || ''
})

const resolveCoinsForPurchase = () => {
  if (selectedPackage.value?.coins) {
    return {
      coins: selectedPackage.value.coins,
      displayCoins: selectedPackage.value.coins,
      source: 'package',
      couponCode: null
    }
  }

  const couponData = couponResult.value?.data
  const coupon = couponData?.coupon
  if (coupon && (coupon.type === 'free_coins' || coupon.type === 'user_related')) {
    const possibleValues = [
      coupon.coins,
      coupon.value,
      coupon.additionalCoins,
      coupon.teamProCoins
    ]

    for (const rawValue of possibleValues) {
      if (rawValue === undefined || rawValue === null) continue
      const numericValue = Number(rawValue)
      if (!Number.isNaN(numericValue) && numericValue >= 0) {
        const awardedCoins = Math.round(numericValue)
        return {
          coins: 0,
          displayCoins: awardedCoins,
          source: 'coupon',
          couponCode: coupon.code || couponData?.code || couponData?.couponCode || null
        }
      }
    }

    return {
      coins: 0,
      displayCoins: 0,
      source: 'coupon',
      couponCode: coupon.code || couponData?.code || couponData?.couponCode || null
    }
  }

  return {
    coins: null,
    displayCoins: 0,
    source: null,
    couponCode: null
  }
}

const isFreeUpgrade = computed(() => {
  return couponResult.value?.success && 
         (couponResult.value.data.coupon.type === 'free_coins' || couponResult.value.data.coupon.type === 'user_related') &&
         priceData.value.totalPrice === 0
})

const canProceed = computed(() => {
  switch (currentStep.value) {
    case 0:
      return selectedPackage.value !== null || (couponResult.value?.success && (couponResult.value.data.coupon.type === 'free_coins' || couponResult.value.data.coupon.type === 'user_related'))
    case 1:
      return isFreeUpgrade.value || validateBillingData()
    case 2:
      return true
    default:
      return false
  }
})

const selectPackage = (packageItem) => {
  selectedPackage.value = packageItem
  calculatePriceData(packageItem, couponResult.value)
  nextStep()
}

const proceedToPayment = () => {
  if (validateBillingData()) {
    nextStep()
  }
}

const onCouponValidated = (result) => {
  couponResult.value = result
  if (result?.success) {
    const normalizedCode = result.data?.coupon?.code || result.data?.code || result.data?.couponCode
    if (normalizedCode) {
      couponCode.value = normalizedCode.trim().toUpperCase()
    }
  }
  if (result?.success && (result.data.coupon.type === 'free_coins' || result.data.coupon.type === 'user_related')) {
    calculatePriceData(selectedPackage.value, result)
  }
}

const onCouponCleared = () => {
  couponResult.value = null
  if (selectedPackage.value) {
    calculatePriceData(selectedPackage.value, null)
  }
}

const onPaymentMethodChanged = (method) => {
  paymentMethod.value = method
}

const onTermsChanged = (accepted) => {
  allTermsAccepted.value = accepted
}

const onBillingDataChanged = (newBillingData) => {
  // Update billing data when form changes
  billingData.value = { ...billingData.value, ...newBillingData }
}

// Load available coin packages based on pricing rules
const loadCoinPackages = async () => {
  try {
    const response = await pricingApi.getCoinPricingRules()
    const rules = Array.isArray(response) ? response : response.data || response.rules || []

    const standardCoinAmounts = [10, 100, 1000]
    
    if (rules.length > 0) {
      coinPackages.value = standardCoinAmounts.map((coins) => {
        const matchingRule = rules.find((rule) => {
          const minCoins = rule.minCoins || 0
          const maxCoins = rule.maxCoins || Infinity
          return coins >= minCoins && coins <= maxCoins
        })

        if (matchingRule) {
          const pricePerCoin = matchingRule.pricePerCoin
          let bruttoPricePerCoin
          let price

          if (matchingRule.priceType === 'netto') {
            const vatAmount = pricePerCoin * 0.19
            bruttoPricePerCoin = pricePerCoin + vatAmount
            price = coins * bruttoPricePerCoin
          } else {
            bruttoPricePerCoin = pricePerCoin
            price = coins * pricePerCoin
          }

          return {
            coins: coins,
            price: Math.round(price * 100) / 100,
            pricePerCoin: Math.round(bruttoPricePerCoin * 1000) / 1000,
            rule: matchingRule,
          }
        } else {
          const bruttoPricePerCoin = 0.142
          const totalPrice = coins * bruttoPricePerCoin

          return {
            coins: coins,
            price: Math.round(totalPrice * 100) / 100,
            pricePerCoin: bruttoPricePerCoin,
            rule: null,
          }
        }
      })
    } else {
      coinPackages.value = standardCoinAmounts.map(coins => ({
        coins: coins,
        price: Math.round(coins * 0.142 * 100) / 100,
        pricePerCoin: 0.142,
        rule: null,
      }))
    }
  } catch (error) {
    console.error('Error loading coin packages:', error)
    coinPackages.value = [
      { coins: 10, price: 1.42, pricePerCoin: 0.142, rule: null },
      { coins: 100, price: 14.16, pricePerCoin: 0.142, rule: null },
      { coins: 1000, price: 141.6, pricePerCoin: 0.142, rule: null },
    ]
  }
}

const processPurchase = async () => {
  try {
    if (!allTermsAccepted.value) {
      showError('Bitte akzeptiere alle Bedingungen, um fortzufahren.')
      return
    }

    const { coins: coinsToPurchase, displayCoins, source, couponCode: resolvedCouponCode } = resolveCoinsForPurchase()
    const finalCouponCodeRaw = couponCode.value?.trim() || resolvedCouponCode || null
    const finalCouponCode = finalCouponCodeRaw ? finalCouponCodeRaw.toUpperCase() : null
    const coinsForMessaging = typeof displayCoins === 'number' ? displayCoins : (coinsToPurchase ?? 0)

    if (coinsToPurchase === null) {
      showError('Bitte wähle ein Quittungspaket oder gib einen gültigen Gutschein ein.')
      return
    }

    if (source !== 'coupon' && (!coinsToPurchase || coinsToPurchase <= 0)) {
      showError('Bitte wähle ein gültiges Quittungspaket aus.')
      return
    }

    if (source === 'coupon' && !finalCouponCode) {
      showError('Der kostenlose Gutschein konnte nicht angewendet werden. Bitte versuche es erneut.')
      return
    }

    const finalPaymentMethod = priceData.value.totalPrice > 0 ? paymentMethod.value : 'free'
    
    await handleCoinPurchase(
      coinsToPurchase,
      finalPaymentMethod,
      finalCouponCode,
      coinsForMessaging
    )
    
    showSuccess('Quittungskauf erfolgreich abgeschlossen!')
    router.push('/teams')
  } catch (error) {
    console.error('Purchase error:', error)
    showError('Fehler beim Kauf. Bitte versuche es erneut.')
  }
}

const handleCoinPurchase = async (coins, paymentMethod, couponCode = null, awardedCoins = null) => {
  try {
    let lastBillingData = null
    const coinsForMessage = typeof awardedCoins === 'number' ? awardedCoins : coins
    
    if (paymentMethod !== 'free' && !couponCode) {
      // For paid purchases, use current form data if saveForFuture is true, otherwise load from API
      if (billingData.value.saveForFuture && hasFilledBillingData.value) {
        lastBillingData = getBillingDataForApi()
      } else {
        try {
          const { getInvoiceData } = await import('@/api/invoiceData')
          const response = await getInvoiceData()
          if (response.success && response.data.invoiceData) {
            lastBillingData = mapInvoiceDataToApi(response.data.invoiceData)
          }
        } catch (error) {
          console.warn('Konnte Rechnungsdaten nicht laden:', error)
        }
      }
    } else if (paymentMethod === 'free' && couponCode) {
      const hasBillingData = billingData.value.firstName && 
                            billingData.value.lastName && 
                            billingData.value.street && 
                            billingData.value.number && 
                            billingData.value.zip && 
                            billingData.value.city && 
                            billingData.value.country && 
                            billingData.value.email
      
      if (hasBillingData) {
        lastBillingData = getBillingDataForApi()
      } else {
        lastBillingData = {}
      }
    }

    const { purchaseCoins } = await import('@/api/billing')
    const result = await purchaseCoins(
      coins,
      lastBillingData,
      couponCode,
      paymentMethod
    )

    if (result.requiresPayment && result.paypalOrder) {
      showSuccess('PayPal-Zahlung wird vorbereitet...')
      window.open(result.paypalOrder.approvalUrl || result.paypalOrder.redirectUrl, '_self', '')
    } else {
      // Save billing data if checkbox is checked
      if (billingData.value.saveForFuture && hasFilledBillingData.value) {
        try {
          const { saveInvoiceData } = await import('@/api/invoiceData')
          await saveInvoiceData(getBillingDataForApi())
        } catch (error) {
          console.warn('Konnte Rechnungsdaten nicht speichern:', error)
        }
      }
      
      if (paymentMethod === 'free' || couponCode) {
        showSuccess(`${coinsForMessage} Quittungen erfolgreich gekauft! Das kostenlose Upgrade wurde erfolgreich angewendet.`)
      } else if (paymentMethod === 'bank_transfer') {
        showSuccess(`${coinsForMessage} Quittungen erfolgreich gekauft! Die Zahlungsaufforderung wurde per E-Mail versendet.`)
      } else {
        showSuccess(`${coinsForMessage} Quittungen erfolgreich gekauft!`)
      }
    }
  } catch (error) {
    console.error('Coin purchase error:', error)
    throw error
  }
}

onMounted(async () => {
  await loadCoinPackages()
  await loadLastBillingData()
  
  // Handle query parameters
  const { couponCode: queryCouponCode, coupon } = route.query
  
  // Handle both couponCode and coupon parameters
  const finalCouponCode = queryCouponCode || coupon
  if (finalCouponCode) {
    couponCode.value = finalCouponCode
    if (finalCouponCode.trim()) {
      await validateCoupon('coin_purchase')
    }
  }
  
  loading.value = false
})
</script>

<style scoped>
/* Additional styles can be added here if needed */
</style>
